import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { checkFolderConnection, fetchPanoptoSessions } from '../../services/api/panoptoService'; 
import { retryAsync } from "../../utils/RetryAsync"
import { triggerPiepeline, PipelineRun, cancelPipeline } from '../../services/api/virtualAssistant'; 
import { FolderData } from "../../types/PanoptoData";
import { Pipeline } from "../../types/pipelineData";
import CustomModal from "../../components/CustomModal";
import WarningModal from "../../components/WarningModal";
import { getSafeImageSrc } from '../../utils/imageUtils';
import PanoptoSidebar from './PanoptoSidebar';
import PanoptoConnectionTab from './PanoptoConnectionTab';
import PanoptoPreviewTab from './PanoptoPreviewTab';
import PanoptoStatusFooter from './PanoptoStatusFooter';

interface PanoptoFolderProps {
  isPanoptoModalOpen: boolean;
  setPanoptoModalOpen: (isOpen: boolean) => void;
  panoptos: FolderData[];
  setPanoptos: React.Dispatch<React.SetStateAction<FolderData[]>>;
  updateExcludedItems: (folderId: number, uncheckedIds: string[]) => void;
  triggerAutoSave: () => void; 
  isPanoptoRNightly: boolean
  setIsPanoptoRNightly: React.Dispatch<React.SetStateAction<boolean>>;
  assistant: any;
  isPanoptoFullLoad: boolean
  setIsPanoptoFullLoad: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ConnectionResponse {
  message: string;
  data: any;
  connection_status: 'success' | 'failure';
}

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  panoptoId: number;
}

const PanoptoFolder: React.FC<PanoptoFolderProps> = ({
  isPanoptoModalOpen,
  setPanoptoModalOpen,
  panoptos,
  setPanoptos,
  updateExcludedItems,
  triggerAutoSave,
  isPanoptoRNightly,
  setIsPanoptoRNightly,
  assistant,
  isPanoptoFullLoad,
  setIsPanoptoFullLoad
}) => {
  const handleUncheckedItems = (folderId: number) => (uncheckedItemIds: string[]) => {
    if (uncheckedItemIds.length > 0) {
      updateExcludedItems(folderId, uncheckedItemIds);
      triggerAutoSave(); 
      setPendingSave(true);
    }
  };

  const [selectedFolder, setselectedFolder] = useState<number>(1);
  const [contextMenu, setContextMenu] = useState<ContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    panoptoId: 0
  });

  const fetchCalledRef = useRef<{ [key: number]: boolean }>({});
  const [pipelineData, setPipelineData] = useState<Pipeline[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [getDataClickTime, setGetDataClickTime] = useState<Date | null>(null);
  const [gettingData, setGettingData] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<null | {
    progress: number;
    status: string;
    error?: string;
  }>(null);
  const [isGettingDataCalled, setIsGetDataCallled] = useState(true);
  const [systemConfig, setSystemConfig] = useState<{ Id: string; ConfigValue: string; }[]>([]);
  const [safeAiIconSrc, setSafeAiIconSrc] = useState<string>(assistant?.app_ai_face);
  const [modalRefreshNModal, setModalRefreshNModal] = useState(false);
  const [cancellingPipeline, setCancellingPipeline] = useState(false);

  const contextMenuRef = useRef<HTMLDivElement>(null);

  const [customModal, setCustomModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "confirmation",
  });

  const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000

  const getCachedData = (key: string) => {
    const item = sessionStorage.getItem(key);
    if (!item) return null;

    const { value, timestamp } = JSON.parse(item);
    if (Date.now() - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(key);
      return null;
    }
    return value;
  };

  // Update safe AI icon source
  useEffect(() => {
    const updateSafeSource = async (): Promise<void> => {
      const src = assistant?.app_ai_face;
      const safeSrc = await getSafeImageSrc(src);
      setSafeAiIconSrc(safeSrc);
    };
    updateSafeSource();
  }, [assistant?.app_ai_face]);

  useEffect(() => {
    const config = getCachedData('systemConfig');
    if (config) {
      setSystemConfig(config);
    }
  }, []);

  // Initialize folders
  useEffect(() => {
    if(panoptos.length === 0){
      addNewPanoptoFolder();
    }
    if(panoptos.length > 0){
      panoptos.forEach((panopto) => {
        if (panopto.panopto_id && panopto.connectionState === 'connected' && !panopto.activeTab) {
          setPanoptos(prev => prev.map(c => 
            c.id === panopto.id ? { ...c,  activeTab: 'CONNECT', url: String(panopto.panopto_id)} : c
          ));
        }
        if (
          panopto.connectionState === 'connected' && 
          !fetchCalledRef.current[panopto.id] &&
          (!panopto.panoptoItems || !panopto.panoptoItems.files?.length)
        ) {
          const filesArray = panopto.panoptoItems?.files || [];
          const checkArray = Object.values(filesArray);
          const hasDetailKey = Object.values(filesArray).some(item => item?.detail != null);
          if (Array.isArray(checkArray) && checkArray.length === 0) {
            fetchPanoptoItems(panopto.id);
            fetchCalledRef.current[panopto.id] = true; 
          }
        }
      });
    }
  }, [panoptos]);

  // Pipeline data fetching
  useEffect(() => {
    fetchPipelineData();
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  
    if (currentStatus?.status !== 'Complete' && currentStatus?.status !== 'Cancelled') {
      if(isGettingDataCalled){
        intervalRef.current = setInterval(() => {
          fetchPipelineData();
        }, 10 * 1000);
      }
    }
  
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentStatus?.status, isGettingDataCalled]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu]);

  const addNewPanoptoFolder = () => {
    const newOrder = panoptos.length + 1;
    const nextId = Math.max(...panoptos.map(panopto => panopto.id), 0) + 1;
    setPanoptos(prev => [...prev, {
      id: nextId,
      panopto_id:'',
      url: '',
      order: newOrder,
      panopto_name:'',
      activeTab: 'CONNECT',
      connectionState: 'idle',
      excludedItems: [],
    }]);
    setselectedFolder(nextId);
  };

  const fetchPanoptoItems = async (panoptoId: number) => {
    const panopto = panoptos.find(c => c.id === panoptoId);
    const panopto_id = panopto?.panopto_id;
    if (!panopto_id) {
      return;
    }
    if (!panopto || panopto.connectionState !== 'connected') return;
  
    setPanoptos(prev => prev.map(c => 
      c.id === panoptoId ? { ...c, isLoadingItems: true } : c
    ));
  
    try {
      const [filesData] = await Promise.all([
        retryAsync(() => fetchPanoptoSessions(panopto_id), 3, 1500),
      ]);
      setPanoptos(prev => {
        const updatedState = prev.map(c =>
          c.id === panoptoId
            ? {
                ...c,
                panoptoItems: {
                  ...c.panoptoItems,
                  files: filesData || [],
                },
                isLoadingItems: false,
                error: undefined,
                activeTab: "PREVIEW" as "PREVIEW",
              }
            : c
        );
        return updatedState;
      });

    } catch (error) {
      setPanoptos(prev => prev.map((panopto) =>
        panopto.id === panoptoId
          ? { 
              ...panopto, 
              isLoadingItems: false, 
              error: 'Failed to fetch items. Please try again.' ,
            }
          : panopto
      ));
    } 
  };

  const fetchPipelineData = async () => {
    const pipeline_name = "panopto_to_vectordb";
    if(assistant.app_id){
      const resPipelineRun = await PipelineRun( pipeline_name, assistant.app_id);

      // ALWAYS store ALL pipelines for history display
      setPipelineData(resPipelineRun);

      // Filter: Only consider pipelines created AFTER user clicked "Get Data Now" for STATUS determination
      let relevantPipeline = resPipelineRun;
      if (getDataClickTime && resPipelineRun.length > 0) {
        relevantPipeline = resPipelineRun.filter((p: any) => {
          const pipelineCreatedAt = new Date(p.created_ts);
          return pipelineCreatedAt >= getDataClickTime;
        });

        // If no new pipelines found yet, keep polling but don't show old status
        if (relevantPipeline.length === 0 && gettingData) {
          setCurrentStatus({ progress: 20, status: "In Progress" });
          setIsGetDataCallled(true);
          return;  // Early return - pipelineData still has all history
        }
      }

      // Use relevantPipeline ONLY for status determination
      if (relevantPipeline && relevantPipeline.length > 0) {
        setIsGetDataCallled(true);

        const backendStatus = relevantPipeline[0].status;
        let status = '';

        // Map backend status to UI status - ALWAYS check actual status first
        if(backendStatus === 'STARTED')
            status = 'In Progress';
        if(backendStatus === 'failed')
            status = 'Failed';
        if(backendStatus === 'success')
          status = 'Complete';
        if (backendStatus === 'cancelled') {
          status = 'Cancelled';
          setCancellingPipeline(false); // Reset cancelling state
          setGettingData(false);
        }
        if (backendStatus === 'cancelling') {
          status = 'Cancelling';
          setCancellingPipeline(true);
        }
        if (backendStatus === 'cancel_failed') {
          status = 'Failed';
          setCancellingPipeline(false);
        }

        setCurrentStatus({ progress: 20, status: status });
      }
      else{
        if(gettingData){
          setCurrentStatus({ progress: 20, status: "In Progress" });
          setIsGetDataCallled(true);
        }
        else{
          setCurrentStatus({ progress: 20, status: "" });
          setIsGetDataCallled(false);
        }
      }
    }
  };

  const handleCancelPipeline = async () => {
    try {
      setCancellingPipeline(true);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'Cancelling' }));

      await cancelPipeline("panopto_to_vectordb", assistant.app_id);

      // Poll for status update after cancel request
      setTimeout(() => {
        fetchPipelineData();
      }, 2000);
    } catch (error) {
      console.error('Failed to cancel pipeline:', error);
      setCancellingPipeline(false);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'In Progress' }));
      
      setCustomModal({
        isOpen: true,
        title: "Cancel Failed",
        message: "Failed to cancel the operation. Please try again.",
        type: "error",
      });
    }
  };

  const handleGetDataNow = async (type: 'azure' | 'azure') => {
    setGettingData(true);
    setPendingSave(false);
    setGetDataClickTime(new Date());   
    setCurrentStatus({ progress: 20, status: 'In Progress' });
    
    let queue_name = systemConfig.find(item => item.Id === 'panopto_queue_name')?.ConfigValue ?? '';
    if (type === 'azure') {
      queue_name = systemConfig.find(item => item.Id === 'panopto_queue_name')?.ConfigValue ?? '';
    }
    const run_type = 'Adhoc';
    const full_load_value = String(isPanoptoFullLoad) === 'true' ? 'false' : 'true';
    if(queue_name){
      const res = await triggerPiepeline( assistant.app_id, run_type, queue_name,full_load_value);
      setTimeout(() => {
        fetchPipelineData();
      }, 30000); 
    }      
  };

  const handleChange = () => {
    if(!isPanoptoRNightly){
      if(assistant?.app_publishstatus?.toUpperCase() === 'UNPUBLISHED')
        setModalRefreshNModal(true)
    }
    setIsPanoptoRNightly(!isPanoptoRNightly);
    triggerAutoSave();
  };

  const showConfirmationModal = (message: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: "Save Confirmation",
      message,
      type: "confirmation",
      onConfirm,
    });
  };

  const handleConnect = async (panoptoId: number, source: 'click' | 'loop') => {
    const panopto = panoptos.find(c => c.id === panoptoId);
    
    if (!panopto?.url) {
      setPanoptos((prev) =>
        prev.map((c) =>
          c.id === panoptoId
            ? { ...c, UrlError: 'Please enter a valid URL.' }
            : c
        )
      );
      return;
    }

    const isCourseAlreadyConnected = panoptos.some(c => c.url === panopto.url && c.panopto_id !== "");

    if (isCourseAlreadyConnected) {
      setPanoptos((prev) =>
        prev.map((c) =>
          c.id === panoptoId
            ? { ...c, UrlError: 'This panopto folder is already connected.' }
            : c
        )
      );
      return;
    }
  
    setPanoptos((prev) =>
      prev.map((c) =>
        c.id === panoptoId ? { ...c, UrlError: undefined } : c
      )
    );
  
    setPanoptos(prev => prev.map(panopto => 
      panopto.id === panoptoId 
        ? { ...panopto, connectionState: 'connecting' } 
        : panopto
    ));
  
    try {
      const result: ConnectionResponse = await checkFolderConnection(panopto.url);
  
      if (result.connection_status === 'success') {
        const sessions = await fetchPanoptoSessions(result.data.Id);
        
        setPanoptos(prev => prev.map(c => 
          c.id === panoptoId 
            ? { 
                ...c, 
                connectionState: 'connected', 
                panopto_id: result.data.Id,
                panopto_name: result.data.Name,
                panoptoItems: {
                  ...c.panoptoItems,
                  files: sessions || [],
                },
                isLoadingItems: false,
                error: undefined,
              } 
            : c
        ));
        
        setTimeout(() => {
          setPanoptos(prev => prev.map(panopto => 
            panopto.id === panoptoId ? { ...panopto, activeTab: 'PREVIEW' } : panopto
          ));
          triggerAutoSave(); 
          if(source === 'click')
            setPendingSave(true)
          else if (!isGettingDataCalled)
            setPendingSave(true)
        }, 2000);
        
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setPanoptos(prev => prev.map((panopto) =>
        panopto.id === panoptoId
          ? { 
              ...panopto, 
              connectionState: 'idle', 
              UrlError: error instanceof Error 
                ? error.message 
                : 'Failed to connect. Please try again.'
            }
          : panopto
      ));
    }
  };

  const setActiveTab = (panoptoId: number, tab: 'CONNECT' | 'PREVIEW') => {
    setPanoptos(prev => prev.map(panopto => 
      panopto.id === panoptoId ? { ...panopto, activeTab: tab } : panopto
    ));
  };

  const handleClose = () => {
    const hasConnectedPanopto = panoptos?.some(w => w.connectionState === "connected");
    const hasUnsavedChanges = !pipelineData || pipelineData.length === 0 || pendingSave;

    if (hasConnectedPanopto && hasUnsavedChanges) {
      showConfirmationModal("Your changes have not been saved. Are you sure you want to close?", async () => {
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
        setPanoptoModalOpen(false);
      });
    } else {
      setPanoptoModalOpen(false);
    }
  };

  const sortedFolders = [...panoptos].sort((a, b) => a.order - b.order);
  const sortedFolderData = panoptos.find(c => c.id === selectedFolder);
  const maxFolders = Number(systemConfig.find(item => item.Id === 'va_max_allowed_panopto_folders')?.ConfigValue) || 6;

  return (
    <div
      className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-60 ${
        isPanoptoModalOpen ? 'block' : 'hidden'
      }`}
      onClick={() => {
        setContextMenu(prev => ({ ...prev, visible: false }));
        setPanoptoModalOpen(false);
      }}
    >
      <div
        className="bg-white w-[90%] h-[80%] shadow-lg rounded-lg flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="pl-3 pr-3 pb-3 pt-2 sticky top-0 z-10 shadow-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <img src="/panopto-logo.svg" alt="Panopto Logo" className="h-5 w-5" />
              <h2 className="text-xl font-semibold text-gray-800">Panopto folders</h2>
            </div>
            <button
              onClick={() => handleClose()}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-600 mt-2 ml-8">
            Setup your Chatbot to train itself with content on your Panopto folders.
            This includes only published content if and when it is released to students.
            You can specify up to {maxFolders} panopto folders.
          </p>
        </div>

        <div className="overflow-auto mt-[2%] h-full">
          <div className="flex flex-1 max-h-[85%] min-h-[85%]">
            {/* Left Sidebar */}
            <PanoptoSidebar
              sortedFolders={sortedFolders}
              selectedFolder={selectedFolder}
              setselectedFolder={setselectedFolder}
              contextMenu={contextMenu}
              setContextMenu={setContextMenu}
              contextMenuRef={contextMenuRef}
              addNewPanoptoFolder={addNewPanoptoFolder}
              setPanoptos={setPanoptos}
              triggerAutoSave={triggerAutoSave}
              maxFolders={maxFolders}
              currentStatus={currentStatus}
              systemConfig={systemConfig}
              assistant={assistant}
              isPanoptoFullLoad={isPanoptoFullLoad}
              setPendingSave={setPendingSave}
              setCustomModal={setCustomModal}
              fetchPipelineData={fetchPipelineData}
              panoptos={panoptos}
            />

            {/* Main Content */}
            <div className="flex-1 overflow-hidden">
              <div className="sticky top-0 border-b border-gray-200">
                <div className="flex px-6">
                  <button
                    className={`px-4 py-2 border-b-2 font-medium ${
                      sortedFolderData?.activeTab === 'CONNECT' 
                        ? 'border-black text-black' 
                        : 'border-transparent text-gray-500'
                    }`}
                    onClick={() => selectedFolder && setActiveTab(selectedFolder, 'CONNECT')}
                  >
                    CONNECT
                  </button>
                  <button
                    className={`px-4 py-2 border-b-2 font-medium ${
                      sortedFolderData?.activeTab === 'PREVIEW' 
                        ? 'border-black text-black' 
                        : 'border-transparent text-gray-500'
                    }`}
                    onClick={() => selectedFolder && sortedFolderData?.connectionState === 'connected' && 
                      setActiveTab(selectedFolder, 'PREVIEW')
                    }
                  >
                    PREVIEW
                  </button>
                </div>
              </div>
              
              <div className="p-4 mb-[5%]">
                {sortedFolderData?.activeTab === 'CONNECT' && (
                  <PanoptoConnectionTab
                    sortedFolderData={sortedFolderData}
                    setPanoptos={setPanoptos}
                    selectedFolder={selectedFolder}
                    triggerAutoSave={triggerAutoSave}
                    setPendingSave={setPendingSave}
                    systemConfig={systemConfig}
                    safeAiIconSrc={safeAiIconSrc}
                    assistant={assistant}
                    isGettingDataCalled={isGettingDataCalled}
                    handleConnect={handleConnect}
                    panoptos={panoptos}
                  />
                )}

                {sortedFolderData?.activeTab === 'PREVIEW' && (
                  <PanoptoPreviewTab
                    sortedFolderData={sortedFolderData}
                    updateExcludedItems={updateExcludedItems}
                    triggerAutoSave={triggerAutoSave}
                    setPendingSave={setPendingSave}
                    setPanoptos={setPanoptos}
                    handleUncheckedItems={handleUncheckedItems}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <PanoptoStatusFooter
            isPanoptoRNightly={isPanoptoRNightly}
            handleNightlyChange={handleChange}
            currentStatus={currentStatus}
            panoptos={panoptos}
            pendingSave={pendingSave}
            handleGetDataNow={handleGetDataNow}
            pipelineData={pipelineData}
            assistant={assistant}
            gettingData={gettingData}
            onCancelPipeline={handleCancelPipeline}
            cancellingPipeline={cancellingPipeline}
          />
        </div>

        <CustomModal
          isOpen={customModal.isOpen}
          onClose={() => setCustomModal({ ...customModal, isOpen: false })}
          onConfirm={customModal.onConfirm}
          title={customModal.title}
          message={customModal.message}
          type={customModal.type}
        />
        
        <WarningModal
          isOpen={modalRefreshNModal}
          onClose={() => setModalRefreshNModal(false)}
          message="Data will be refreshed nightly only if the assistant is published."
        />
      </div>
    </div>
  );
};

export default PanoptoFolder;